import type { AccountUserGroup } from "@/enterpriseAccounts/visibility/accountVisibilityDomain";
import { isOk, Result } from "@/common/common";
import { StringFieldModel } from "@/common/commonForms";
import { DCEvent } from "@/events/eventsDomain";
import { orderlessPredicate } from "@/utils/filter";
import type { TMessage } from "@/i18n/tt";

import {
  isMapTemplate,
  isProjectTemplate,
  MapTemplateContent,
  MapTemplateDTO,
  ProjectTemplateAttachmentDTO,
  ProjectTemplateContent,
  ProjectTemplateDTO,
  ProjectTemplateRecipientCategory,
  type TemplateContent,
  type TemplateDTO,
  type TemplateType,
} from "@/templates/templateDomain";
import type { MapTemplateFilter, ProjectTemplateFilter } from "@/templates/templateListDomain";
import { templateApiClient } from "@/templates/templateApiClient";

export type TemplateFilter =
  | { type: TemplateType.PROJECT; filter: ProjectTemplateFilter }
  | { type: TemplateType.MAP; filter: MapTemplateFilter };

export type TemplateSource =
  | { type: "account"; accountId: string }
  | {
      type: "company";
      orgNo: string;
      companyId: string;
      accountId: string;
      dialog?: ProjectTemplateDialogState;
    };

export type TemplateState = {
  source: TemplateSource;
  templatesResult?: Result<TemplateDTO[]>;
  groups: AccountUserGroup[];
  search: StringFieldModel;
  filter: TemplateFilter;
  panelTabId?: string;
  contents: { [templateId: string]: Result<TemplateContent> };
  selected?: SelectedTemplateState;
};

export type SelectedTemplateState = ProjectTemplateState | MapTemplateState;

export const updateTemplateState = (
  f: (state: TemplateState) => Partial<TemplateState>,
): DCEvent => ({
  type: "update-store",
  data: { templates: (state) => state && { ...state, ...f(state) } },
});

export const withTemplateState = (f: (state: TemplateState) => DCEvent[]): DCEvent => ({
  type: "with-store",
  events: (store) => (store.templates ? f(store.templates) : []),
});

export const matchesSearch = (state: TemplateState) =>
  orderlessPredicate<TemplateDTO>((template) => template.name, state.search.inputValue);

export const templateContentEvent = (
  source: TemplateSource,
  templateId: string,
  callbacks: {
    onSuccess: (res: { data: TemplateContent }) => DCEvent[];
    onFailure: () => DCEvent[];
  },
): DCEvent =>
  templateApiClient.getContent(
    templateId,
    source.type === "company" ? source.companyId : undefined,
    callbacks,
  );

export const savedTemplates = (state: TemplateState): TemplateDTO[] =>
  isOk(state.templatesResult) ? state.templatesResult.data : [];

export const mapTemplates = (state: TemplateState): MapTemplateDTO[] =>
  savedTemplates(state).filter(isMapTemplate);

export const projectTemplates = (state: TemplateState): ProjectTemplateDTO[] =>
  savedTemplates(state).filter(isProjectTemplate).map(checkedContent);

export const checkedContent = (template: ProjectTemplateDTO): ProjectTemplateDTO => {
  const { decisionIds, documentIds, attachmentIds } = template.data.excluded;
  return {
    ...template,
    data: {
      ...template.data,
      matters: template.data.matters
        .filter((matter) => !decisionIds.includes(matter.id))
        .map((matter) => ({
          ...matter,
          attachments: checkedFiles(matter.attachments, attachmentIds),
        })),
      documents: template.data.documents
        .filter((document) => !documentIds.includes(document.id))
        .map((document) => ({
          ...document,
          attachments: checkedFiles(document.attachments, attachmentIds),
        })),
    },
  };
};

export const checkedFiles = (
  attachments: ProjectTemplateAttachmentDTO[],
  excludedIds: string[],
): ProjectTemplateAttachmentDTO[] =>
  attachments.filter((attachment) => !excludedIds.includes(attachment.id));

export type ProjectTemplateState = {
  type: TemplateType.PROJECT;
  template: ProjectTemplateDTO;
  content?: Result<ProjectTemplateContent>;
  selectedMatterId?: string;
  previewAs?: ProjectTemplateRecipientCategory;
  selectedRecipientId?: string;
  scopeRemovals: ScopeRemovals;
};

export type MapTemplateState = {
  type: TemplateType.MAP;
  template: MapTemplateDTO;
  content?: Result<MapTemplateContent>;
  previewCompany?: TemplatePreviewCompany;
  companySearch?: string;
};

export type TemplatePreviewCompany = { companyId: string; orgNo: string; name: string };

export type ScopeRemovals = { decisionIds: string[]; attachmentIds: string[] };

export type TemplateScope = {
  templateId: string;
  projectName: StringFieldModel;
};

export type ProjectTemplateDialogState = {
  title: TMessage;
  scope?: TemplateScope;
  isCreating: boolean;
};
